import { useExperimentsStore } from '@shared/stores/experiments.js'
import { useClientStore } from '@shared/stores/client.js'
import { toPascalCase } from '@/utils/stringParsing'
import { useLogger } from 'vue-logger-plugin'
import { useRoute } from 'vue-router'
import { useStyleProfileStore } from '@/stores/styleProfile.js'
import { inMobileWebView } from '@/utils/mobileApp.js'
import { useOnboardingStore } from '@/stores/onboarding.js'
import { storeToRefs } from 'pinia'

export default function useAnalytics () {
  const logger = useLogger()
  const { getAnalyticsData } = useExperimentsStore()
  const client = useClientStore()
  const styleProfile = useStyleProfileStore()

  const route = useRoute()

  async function track (eventName, data = {}) {
    if (typeof window.analytics !== 'undefined') {
      try {
        const experimentData = await getAnalyticsData()
        const eventProperties = {
          ...experimentData,
          ...data
        }
        if (inMobileWebView()) {
          eventProperties.context = 'webview'
        }
        if (client?.email) {
          if (!client.reportEvents) return
          eventProperties.email = client.email
        }
        logger.debug('track() analytics composable', eventName, eventProperties)
        window.analytics.track(eventName, eventProperties)
      } catch (e) {
        logger.error('track() error', e)
      }
    }
  }

  async function trackPageView (pageName, data = {}) {
    await track(`Viewed ${pageName}`, data)
  }

  /** @deprecated Use {@link trackClick} instead */
  async function trackClickEvent (appEntryPoint, label) {
    await track(`Clicked ${toPascalCase(appEntryPoint)} ${label}`)
  }

  /**
   * Track a click event.
   * @param {string} targetName - The name of the target
   * @param {string} targetType - The type of target (e.g. 'button', 'link')
   * @param {object} data - Additional data to send with the event
   */
  async function trackClick (targetName, targetType, data = {}) {
    await track('Click', {
      targetName,
      targetType,
      ...data
    })
  }

  /**
   * Track a page view event.
   * @param {string} pageName - The name of the page
   * @param {object} data - Additional data to send with the event
   */
  async function trackPageViewEvent (pageName, data = {}) {
    await track('Page View', {
      pageName,
      ...data
    })
  }

  async function trackSignUpPageView (name = null) {
    const pageName = name ?? (route.name ? toPascalCase(route.name) : null)
    if (pageName) {
      await trackPageView(pageName, {
        isReferral: !!route.query.r,
        birthDate: styleProfile.birthDate,
        braSizeBand: styleProfile.braSizeBand,
        braSizeCup: styleProfile.braSizeCup,
        heightFeet: styleProfile.heightFeet,
        heightInches: styleProfile.heightInches,
        signupReasons: styleProfile.signupReasons,
        signupEvents: styleProfile.signupEvents,
        avoids: styleProfile.avoids
      })
    }
  }
  /**
   * Track overlay actions
   * @param {string} name - The name of the overlay
   * @param {string} overlayType - The type of overlay (e.g. 'banner', 'modal')
   * @param {string} action - The action taken on the overlay (e.g. 'dismiss', 'click')
   * @param {object} data - Additional data to send with the event
   */
  async function trackOverlayInteraction (name, overlayType, action, data = {}) {
    const allowedActions = ['open', 'dismiss', 'click', 'submit']

    if (!allowedActions.includes(action)) {
      logger.error(`Invalid action "${action}" for overlay "${name}". Allowed actions are: ${allowedActions.join(', ')}`)
      return
    }

    await track('Overlay Interaction', {
      name,
      overlayType,
      action,
      ...data
    })
  }

  function trackOnboardingInteraction (action, data = {}) {
    const { completed, progress } = storeToRefs(useOnboardingStore())
    trackOverlayInteraction('onboarding', 'modal', action, {
      completed: completed.value,
      progress: progress.value,
      ...data
    })
  }

  return {
    track,
    trackPageView,
    trackClickEvent,
    trackClick,
    trackPageViewEvent,
    trackSignUpPageView,
    trackOverlayInteraction,
    trackOnboardingInteraction,
  }
}
